/**
 * Victory banner — full-screen DOM overlay shown when the sim's
 * `state.victoryResult.kind !== 'ongoing'` (v0M3.9 / v0M15.6).
 *
 * The client polls `state.victoryResult` each frame. On the first frame
 * a non-ongoing kind is seen, `show()` renders the banner and returns
 * true so main.ts can also auto-pause the sim driver.
 *
 * v0M15.6 (2026-07-29) — three action buttons per the v0M15 design lock:
 *
 *   Return to Main Menu   → caller confirms + reloads the page.
 *   Continue Watching     → hides the banner + marks it dismissed so it
 *                            does NOT re-show for the same victoryResult
 *                            on subsequent frames. The sim stays paused
 *                            (main.ts set speed=0 on newlyShown); the
 *                            player may click the speed HUD to un-pause
 *                            manually if they want to watch the world
 *                            keep running after the game has been
 *                            decided.
 *   Load Previous Save    → caller opens the save-slot picker in load
 *                            mode; on load, session-storage handoff +
 *                            reload (via the same path used by the
 *                            in-game menu Load).
 *
 * Banner UI-only — no direct save-storage / reload calls. Caller wires
 * the callbacks so this module stays testable and reusable.
 */

import { injectMenuStyles } from './menu-styles';
import { getFactionById } from '../../data/factions';
import type { VictoryResult } from '../../sim';

export interface VictoryBannerCallbacks {
  /** Confirmed by the banner's own confirm dialog. Caller reloads. */
  onReturnToMain(): void;
  /**
   * Caller opens the save-slot picker in load mode; on successful load
   * of a slot, the caller handles the sessionStorage handoff + reload.
   */
  onLoadPreviousSave(): void;
}

export interface VictoryBannerHandle {
  /**
   * Sync the banner to the current victoryResult. Returns true if the
   * banner was newly shown this frame (main.ts can then pause the sim
   * driver). Idempotent.
   *
   * Returns false when:
   *  - result.kind === 'ongoing' (banner hides + resets)
   *  - result unchanged from previous sync (no-op)
   *  - result unchanged AND user already dismissed via Continue Watching
   */
  sync(result: VictoryResult, playerFactionId: number | null): boolean;
  destroy(): void;
}

export function mountVictoryBanner(
  root: HTMLElement,
  cb: VictoryBannerCallbacks,
): VictoryBannerHandle {
  injectMenuStyles();

  const container = document.createElement('div');
  container.className = 'su-victory-overlay';
  container.style.display = 'none';

  const title = document.createElement('h1');
  title.className = 'su-victory-title';
  container.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.className = 'su-victory-subtitle';
  container.appendChild(subtitle);

  // v0M15.6 — 3-button row below the subtitle.
  const buttonRow = document.createElement('div');
  buttonRow.className = 'su-victory-buttons';
  container.appendChild(buttonRow);

  const returnBtn = makeButton('Return to Main Menu', () => {
    if (window.confirm('Return to Main Menu? Any unsaved progress will be lost.')) {
      cb.onReturnToMain();
    }
  });
  const continueBtn = makeButton('Continue Watching', () => {
    // Dismiss the banner; leave the sim paused. Player can un-pause
    // via the speed HUD if they want the sim to continue.
    dismiss();
  });
  const loadBtn = makeButton('Load Previous Save', () => {
    cb.onLoadPreviousSave();
  });
  buttonRow.appendChild(returnBtn);
  buttonRow.appendChild(continueBtn);
  buttonRow.appendChild(loadBtn);

  root.appendChild(container);

  /**
   * Currently-shown result signature (kind + winning team + condition).
   * Null when the banner is hidden (either not-yet-shown or transitioned
   * back to ongoing). Non-null while shownSignature matches the sim's
   * result — sync() returns false and takes no action in that case,
   * which naturally handles the Continue Watching dismissal too: once
   * the container is hidden, sync's same-sig short-circuit keeps it
   * hidden without needing a separate dismissed flag.
   */
  let shownSignature: string | null = null;

  function dismiss(): void {
    container.style.display = 'none';
    // NOTE: intentionally NOT clearing shownSignature — leaving it set
    // is what makes sync's next same-sig call a no-op (keeps the
    // banner hidden while the sim's victoryResult remains unchanged).
  }

  function sync(result: VictoryResult, playerFactionId: number | null): boolean {
    if (result.kind === 'ongoing') {
      if (shownSignature !== null) {
        container.style.display = 'none';
        shownSignature = null;
      }
      return false;
    }

    const sig = resultSignature(result);
    if (shownSignature === sig) {
      // Same result — either already showing (no-op) or dismissed via
      // Continue Watching (container hidden, keep it that way).
      return false;
    }

    if (result.kind === 'team-victory') {
      const isPlayerVictory =
        playerFactionId !== null && result.winningTeamId === playerFactionId;
      const winnerName = safeFactionName(result.winningTeamId);
      title.textContent = isPlayerVictory ? 'Victory' : `${winnerName} Victorious`;
      subtitle.textContent =
        result.condition === 'destroy-capitals'
          ? 'All enemy capitals have fallen.'
          : 'All enemy cities have been razed.';
    } else if (result.kind === 'draw') {
      title.textContent = 'Mutual Elimination';
      subtitle.textContent =
        result.condition === 'destroy-capitals'
          ? 'No capital remains.'
          : 'No city remains.';
    } else {
      // MFix11.5 (2026-07-27) — player-defeated: human player was
      // eliminated but AI factions are still fighting.
      title.textContent = 'Defeat';
      subtitle.textContent =
        result.condition === 'destroy-capitals'
          ? 'Your capital has fallen.'
          : 'Your last city has been lost.';
    }

    container.style.display = 'flex';
    shownSignature = sig;
    return true;
  }

  function destroy(): void {
    container.remove();
  }

  return { sync, destroy };
}

/**
 * Compose a stable signature so sync() can detect fresh results across
 * frames. Encodes every field that influences the banner's text.
 */
function resultSignature(result: Exclude<VictoryResult, { kind: 'ongoing' }>): string {
  if (result.kind === 'team-victory') {
    return `team-victory:${result.winningTeamId}:${result.condition}`;
  }
  if (result.kind === 'draw') {
    return `draw:${result.condition}`;
  }
  return `player-defeated:${result.condition}`;
}

function safeFactionName(teamId: number): string {
  try {
    return getFactionById(teamId).name;
  } catch {
    return `Team ${teamId}`;
  }
}

function makeButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'su-victory-btn';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}
